import { useCallback, useEffect, useState } from 'react';

import { convertHHMMToMinutes, convertMinutesToHHMM } from './utils';

import {
  ArrowDownIcon,
  ArrowUpIcon,
  HourglassIcon,
} from '@cobliteam/base-components/src/icons';

import CustomHourInput from './components/CustomHourInput';

import { Button, Dropdown, Typography } from '../../../components';

interface SelectMaxStopTimeProps {
  size?: 'condensed' | 'regular';
  selectedMinutes?: number;
  onChange: (minutes?: number | undefined) => void;
}

enum Arrow {
  UP = 'up',
  DOWN = 'down',
}

const validateInput = (input: string): boolean => {
  if (input.length === 0) return true;

  const [hours, minutes] = input.split('h').map((value) => value.trim());
  if (hours.length != 2 || minutes.length != 2) return false;

  const hoursNumber = Number(hours);
  const minutesNumber = Number(minutes);

  return (
    hoursNumber >= 0 &&
    hoursNumber <= 99 &&
    minutesNumber >= 0 &&
    minutesNumber <= 59
  );
};

const SelectMaxStopTime = ({
  size = 'regular',
  selectedMinutes,
  onChange,
}: SelectMaxStopTimeProps) => {
  const timeStr =
    selectedMinutes !== undefined
      ? convertMinutesToHHMM(selectedMinutes)
      : undefined;
  const [currentInputTime, setCurrentInputTime] = useState<string | undefined>(
    timeStr
  );
  const [appliedTime, setAppliedTime] = useState<string | undefined>(timeStr);
  const [forceClosed, setForceClosed] = useState<boolean>(false);

  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  const hasNotChanged = currentInputTime == timeStr;
  const hasInput = currentInputTime !== undefined;
  const hasValidInput = hasInput && validateInput(currentInputTime);
  const disableApplyButton = hasNotChanged || !hasValidInput;

  const onClick = useCallback(() => {
    setCurrentInputTime(timeStr);
  }, [timeStr]);

  const handleChangeByInput = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setCurrentInputTime(value);
    },
    []
  );

  const handleChangeByArrow = useCallback(
    (arrow: Arrow) => {
      const startHour = currentInputTime || '00h00';
      let [hours, minutes] = startHour.split('h').map(Number);

      if (arrow === Arrow.UP) {
        if (hours !== 99 || minutes !== 59) {
          minutes++;
          if (minutes === 60) {
            minutes = 0;
            hours = (hours + 1) % 100;
          }
        }
      } else {
        if (hours !== 0 || minutes !== 0) {
          minutes--;
          if (minutes === -1) {
            minutes = 59;
            hours = (hours - 1 + 100) % 100;
          }
        }
      }

      const newTime = `${String(hours).padStart(2, '0')}h${String(
        minutes
      ).padStart(2, '0')}`;
      setCurrentInputTime(newTime);
    },
    [currentInputTime]
  );

  const handleClear = useCallback(() => {
    setCurrentInputTime('');
  }, []);

  const handleClose = useCallback(() => {
    setForceClosed(true);
  }, []);

  const handleApply = useCallback(() => {
    const possibleMinutes = currentInputTime
      ? convertHHMMToMinutes(currentInputTime)
      : undefined;
    onChange(possibleMinutes);
    setAppliedTime(currentInputTime);
    handleClose();
  }, [currentInputTime, onChange, handleClose]);

  return (
    <Dropdown
      onOpenChange={onClick}
      forceClose={forceClosed}
      overlay={
        <Dropdown.Overlay className='w-auto'>
          <Dropdown.Header className='px-5'>
            <Button variant='text' disabled={!hasInput} onClick={handleClear}>
              Limpar filtro
            </Button>
          </Dropdown.Header>

          <div className='mx-5 my-3 flex max-h-64 items-center gap-2'>
            <Typography variant='caption-default'>
              Mostrar somente paradas menores que
            </Typography>
            <CustomHourInput
              autoFocus
              placeholder='00h00'
              className='w-20'
              onChange={handleChangeByInput}
              value={currentInputTime}
              endAdornment={
                <div className='flex cursor-pointer flex-col'>
                  <ArrowUpIcon onClick={() => handleChangeByArrow(Arrow.UP)} />
                  <ArrowDownIcon
                    onClick={() => handleChangeByArrow(Arrow.DOWN)}
                  />
                </div>
              }
            />
          </div>

          <Dropdown.Footer>
            <Dropdown.Actions className='px-5'>
              <Button
                variant='text'
                full
                size='condensed'
                onClick={handleClose}
              >
                Cancelar
              </Button>
              <Button
                size='condensed'
                full
                onClick={handleApply}
                disabled={disableApplyButton}
              >
                Aplicar
              </Button>
            </Dropdown.Actions>
          </Dropdown.Footer>
        </Dropdown.Overlay>
      }
    >
      <Button variant='text' size={size} startIcon={<HourglassIcon />}>
        Paradas {appliedTime && '(< ' + appliedTime + ')'}
      </Button>
    </Dropdown>
  );
};

export default SelectMaxStopTime;
